import { useState } from 'react';
import { RegistroCta } from '@/features/auth/components/registro-cta';
import { motion } from 'motion/react';
import { ArrowRight, ChevronDown, Mail, MessageCircle } from 'lucide-react';
import { ScrollReveal } from '@/components/motion/scroll-reveal';
import { contactFaqItems } from '../data/contact-data';
import {
  enlaceDeWhatsApp,
  useContactoPublico,
} from '@/features/solicitudes/hooks/use-contacto-publico';

// ── Contacto directo (D24): sólo si la plataforma lo cargó ──

function ContactCards() {
  const { correo, whatsapp } = useContactoPublico();
  if (!correo && !whatsapp) return null;
  return (
    <div className="space-y-4 mb-8">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
        También puedes escribirnos
      </h3>
      <div className="space-y-3">
        {correo && (
          <a
            href={`mailto:${correo}`}
            className="flex items-center gap-4 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white/60 dark:bg-gray-800/40 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
          >
            <Mail className="w-5 h-5 shrink-0 text-gray-500" aria-hidden="true" />
            <span className="text-sm text-gray-900 dark:text-white">{correo}</span>
          </a>
        )}
        {whatsapp && (
          <a
            href={enlaceDeWhatsApp(whatsapp)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 p-4 rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white/60 dark:bg-gray-800/40 hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
          >
            <MessageCircle className="w-5 h-5 shrink-0 text-gray-500" aria-hidden="true" />
            <span className="text-sm text-gray-900 dark:text-white">WhatsApp {whatsapp}</span>
          </a>
        )}
      </div>
    </div>
  );
}

// ── Mini FAQ ──

function MiniFaq() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="mt-8">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-4">
        Preguntas frecuentes
      </h4>
      <div className="space-y-2">
        {contactFaqItems.map((faq, index) => {
          const isOpen = openIndex === index;
          return (
            <div
              key={index}
              className="rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white/60 dark:bg-gray-800/40 backdrop-blur-sm overflow-hidden"
            >
              <button
                type="button"
                onClick={() => setOpenIndex(isOpen ? null : index)}
                className="flex items-center justify-between w-full px-4 py-3 text-left"
              >
                <span className="text-sm font-medium text-gray-900 dark:text-white pr-4">
                  {faq.question}
                </span>
                <ChevronDown
                  className={`w-4 h-4 shrink-0 text-gray-400 transition-transform duration-200 ${
                    isOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <motion.div
                initial={false}
                animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <p className="px-4 pb-3 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                  {faq.answer}
                </p>
              </motion.div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── CTA banner ──

function CtaBanner() {
  return (
    <div className="mt-8 relative overflow-hidden rounded-xl">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-violet-600 to-blue-700" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.15),_transparent_50%)]" />
      <div className="relative px-6 py-6 text-center">
        <p className="text-white font-semibold mb-1">¿Prefieres verla funcionando?</p>
        <p className="text-white/70 text-sm mb-4">
          Pide tu Mini Prixia: 15 días desde que entras, guiada por misiones.
        </p>
        <RegistroCta className="group inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-white text-blue-700 font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-[0_0_30px_rgba(255,255,255,0.3)]">
          Pide tu Mini Prixia
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </RegistroCta>
      </div>
    </div>
  );
}

// ── Main export ──

export function ContactInfo() {
  return (
    <ScrollReveal delay={0.2}>
      <div>
        <ContactCards />
        <MiniFaq />
        <CtaBanner />
      </div>
    </ScrollReveal>
  );
}
